<template>
  <div v-loading="isLoading">
    <el-table
      :data="teams"
      stripe
    >
      <!-- Peek -->
      <el-table-column type="expand">
        <template slot-scope="props">
          <p style="font-size: 16px; font-weight: 500;">
            <FontAwesomeIcon :icon="['fal', 'code']" />
            &nbsp;&nbsp;API Credentials
            &nbsp;&nbsp;
            <el-button
              v-clipboard:copy="copyApiCredentials(props.row.name, props.row.legacy.key)"
              v-clipboard:success="onCopyApiCredentials"
              v-clipboard:error="onErrorApiCredentials"
              size="small"
              type="primary"
            >
              Copy API Credentials
            </el-button>
          </p>
          <p>
            <strong>Username:</strong>&nbsp;<el-tag type="danger">
              {{ props.row.name }}
            </el-tag>
          </p>
          <p>
            <strong>Key:</strong>&nbsp;<el-tag type="danger">
              {{ props.row.legacy.key }}
            </el-tag>
          </p>
        </template>
      </el-table-column>

      <el-table-column
        label="Name"
        min-width="120px"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          <router-link :to="{ name:'manage-accounts-account-configurations-teams-team', params: { teamId: scope.row.id } }">
            {{ scope.row.name }}
          </router-link>
        </template>
      </el-table-column>

      <el-table-column
        label="Code"
        prop="code"
        width="120px"
      />

      <el-table-column
        label="Description"
        prop="description"
        width="320px"
        show-overflow-tooltip
      />

      <el-table-column
        label="Hidden Services"
        prop="pivot_hide_services_count"
        width="180px"
      />

      <el-table-column
        label="Blocked Autobooks"
        prop="pivot_no_autobooks_count"
        width="180px"
      />

      <el-table-column
        label="Platform"
        prop="open360_only"
        :formatter="(row, col, cellValue) => cellValue ? 'Open360' : 'OpenFreight'"
      />

      <el-table-column
        label="Show As Cost Center"
        prop="show_as_cost_center"
        width="180px"
        :formatter="(row, col, cellValue) => cellValue ? 'Yes' : 'No'"
      />

      <el-table-column
        v-if="canUpdate || canDelete"
        label="Actions"
        align="right"
      >
        <template slot-scope="scope">
          <el-dropdown
            split-button
            size="mini"
            type="primary"
            class="mr-2"
            trigger="click"
            @click="handleImpersonationOpen360(scope.row)"
          >
            Impersonate
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="impersonate(scope.row)">
                Impersonate OpenFreight
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <el-button
            v-if="canDelete"
            size="mini"
            @click.native="editTeam(scope.$index, scope.row)"
          >
            Edit
          </el-button>

          <el-button
            v-if="canDelete"
            size="mini"
            type="danger"
            @click.native="deleteTeam(scope.$index, scope.row)"
          >
            Delete
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <el-pagination
      class="text-center pt-3"
      :current-page.sync="page"
      :page-sizes="[10, 20, 50, 100]"
      :page-size.sync="perPage"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      @size-change="loadTable"
      @current-change="loadTable"
    />

    <!-- Edit Form -->
    <TeamEditForm
      v-if="isShowEditForm"
      ref="teamEditForm"
      :is-show.sync="isShowEditForm"
      :team="team"
      :account-id="accountId"
      @saved="loadTable()"
    />
  </div>
</template>

<script>
import TeamEditForm from '@/components/Application/Manage/Account/TeamEditForm'
import { CREATE, UPDATE, DELETE } from '@/plugins/user'
export default {
  components: {
    TeamEditForm
  },
  props: {
    accountId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      team: null,
      teams: [],
      page: 1,
      perPage: 10,
      total: 0,
      isLoading: false,
      isShowEditForm: false
    }
  },
  computed: {
    canUpdate () {
      return this.$user.can(UPDATE, 'account_team')
    },
    canDelete () {
      return this.$user.can(DELETE, 'account_team')
    }
  },
  created () {
    this.loadTable()
  },
  mounted () {
    this.actions = [
      {
        hidden: this.$user.cannot(CREATE, 'account_team'),
        name: 'Create',
        icon: ['fal', 'plus'],
        event: () => {
          this.team = new this.$sdk.team()
          this.isShowEditForm = true
        }
      },
      {
        name: 'Refresh',
        icon: ['fal', 'sync'],
        event: () => this.loadTable()
      }
    ]
  },
  methods: {
    platformFormatter (row) {
      return row.pivot.platform ? 'Yes' : 'No'
    },
    loadTable () {
      this.isLoading = true
      this.$sdk.team.list(builder => {
        builder.context = { resource: 'account', id: this.accountId }
        builder.page = this.page
        builder.limit = this.perPage
        builder.include = ['account', 'shipper_setting.address', 'shipper_setting.default_service.carrier', 'legacy']
        builder.order = 'uuid:asc'
      })
        .then(res => {
          this.teams = res
          this.total = res.total
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    editTeam (index, row) {
      this.team = row.clone()
      this.isShowEditForm = true
    },
    deleteTeam (index, row) {
      this.$confirm('Are you sure to delete this team?', 'Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        row.delete(builder => {
          builder.context = { resource: 'account', id: this.accountId }
        })
          .then(() => this.$message({ message: 'Successfully deleted', type: 'success' }))
          .finally(() => this.loadTable())
      }).catch(() => {})
    },
    copyApiCredentials (username, key) {
      return `Username: ${username}\nKey: ${key}`
    },
    onCopyApiCredentials () {
      this.$message({ message: 'Copied to clipboard', type: 'success' })
    },
    onErrorApiCredentials () {
      this.$message({ message: 'An error occurred while copying API credentials', type: 'error' })
    },
    async handleImpersonationOpen360 (team) {
      // Request an impersonation token for the team, which will be used by Open360 to authenticate the user
      await this.$sdk.account_team.impersonate(
        team.account.slug,
        (await this.$session.instance).tenancy.id,
        () => {
          this.$message.success('Impersonation Token Generated. Redirecting to Open360...')
        }
      )
    },
    async impersonate (team) {
      window
        .open(
          this
            .$router
            .resolve({
              name: 'legacy-handover',
              query: {
                tenancy: (await this.$session.instance).tenancy.id,
                token: await this.$session.token,
                'impersonation-token': team.legacy.hash
              }
            })
            .href,
          '_blank'
        )
    }
  }
}
</script>

<style>

</style>
